import { MiniDb } from 'jotai-minidb';
import { JWT, checkToken } from './Auth';
import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { jwtDecode } from 'jwt-decode';
import logger from './logger';
import z, { ZodSchema } from 'zod';
import { getHasuraName, isDev, getEnvironment, AUTH0_DOMAINS, AUTH0_CLIENT_IDS, PARTYKIT_HOST } from './constants';
import localforage from 'localforage';
import { DevTools } from 'jotai-devtools';
import { broadcastSessionEvent } from './session';

export const pageSchema = z.object({
  id: z.string(),
  name: z.string(),
  index: z.optional(z.number()),
  pageProducts: z.array(
    z
      .object({
        id: z.string(),
        'grid-id': z.string(),
        'grid-index': z.optional(z.number()),
      })
      .nullable(),
  ),
});

export const packageSchema = z.object({
  id: z.number(),
  name: z.string(),
  source: z.object({
    name: z.string(),
    exchange: z.nullable(z.object({ code: z.number() })),
  }),
  products: z.array(
    z.object({
      id: z.string(),
      artis_type: z.string(),
      code: z.nullable(z.string()),
      description: z.string(),
      eod_product_dep: z.nullable(z.string()),
      logical_code: z.nullable(z.string()),
      maturity: z.string(),
      name: z.string(),
      summary_type: z.object({
        value: z.union([
          z.literal('sum'),
          z.literal('average'),
          z.literal('timespread'),
          z.literal('hourly_weighted_average'),
          z.literal('hourly_weighted_timespread'),
          z.literal('none'),
        ]),
      }),
      product_type: z.nullable(z.union([
        z.literal('box'),
        z.literal('change'),
        z.literal('diff'),
        z.literal('outright'),
        z.literal('timespread'),
        z.literal('crack'),
        z.literal('arb'),
        z.literal('misc'),
      ])),
      uom: z.string(),
      has_shared_cell: z.boolean(),
      geographical_region: z.nullable(z.object({ id: z.number() })),
      commodity_group: z.nullable(z.object({ id: z.number() })),
      commodity_parent_group: z.nullable(
        z.object({ parent: z.object({ id: z.number() }) }),
      ),
      product_configs: z.array(
        z.object({
          formula: z.nullable(z.string()),
          product: z.string(),
          relative_month: z.number(),
        }),
      ),
    }),
  ),
});

export type TPackage = z.infer<typeof packageSchema>;
export type TProduct = TPackage['products'][number];

export type TPage = z.infer<typeof pageSchema>;

export const pageStore = new MiniDb<TPage>({
  name: 'artis-page-db',
  version: 0,
});

export const configSchema = z.object({
  selectedPageIds: z.array(z.string()),
  openWorkSheets: z.array(z.string()),
  packages: z.array(packageSchema),
  token: z.string(),
  lastUpdated: z.nullable(z.number()),
});

export type TConfig = z.infer<typeof configSchema>;
const defaultConfig = {
  selectedPageIds: [],
  openWorkSheets: [],
  packages: [],
  token: '',
  lastUpdated: null,
};

export const configStore = new MiniDb<TConfig>({
  name: 'artis-config-db',
  version: 0,
  initialData: {
    config: defaultConfig,
  },
});

export const uomMapAtom = atom<Record<string, string>>({});

export function useUomMap() {
  return useAtomValue(uomMapAtom);
}

export function useSetUomMap() {
  const set = useSetAtom(uomMapAtom);
  return (map: Record<string, string>) => {
    set(map);
  };
}

// Simple disconnect - clears local data and reloads
export function disconnectFn() {
  const store = getDefaultStore();
  store.set(configStore.set, 'config', (old) => {
    return {
      ...defaultConfig,
      selectedPageIds: old?.selectedPageIds ?? [],
      lastUpdated: old?.lastUpdated ?? null,
    };
  });
  store.set(pageStore.clear);
  setTimeout(() => {
    window.location.reload();
  }, 1000);
}

// Full logout - clears all local data and invalidates token
// fromBroadcast: true when triggered by another add-in's logout broadcast (skip re-broadcasting and PartyKit)
export async function logoutFn(fromBroadcast = false) {
  if (window.location.pathname.includes('/legacy')) {
    window.open(`${window.location.origin}/login?logout=true`, "popupName", "height=600,width=400");
    setTimeout(() => {
      window.location.href = '/';
    });
    return;
  }
  const store = getDefaultStore();
  
  logger.info('Full logout - clearing all data and invalidating token', { fromBroadcast });
  
  // Only broadcast and call PartyKit if this is a user-initiated logout
  if (!fromBroadcast) {
    // Broadcast logout to other add-ins on the same machine
    broadcastSessionEvent({ type: 'user_logged_out' });
    
    // Send logout message to PartyKit to clear server-side cache (fire-and-forget, don't block logout)
    import('get-browser-fingerprint')
      .then(({ default: getBrowserFingerprint }) => {
        const code = String(getBrowserFingerprint());
        const partyKitUrl = `https://${PARTYKIT_HOST}/parties/main/${code}`;
        logger.info('Sending logout to PartyKit server', { room: code });
        
        fetch(partyKitUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ type: 'logout' }),
        }).catch((error) => {
          // CORS errors are expected on some origins - don't block logout
          logger.warn('Failed to send logout to PartyKit (CORS issue?)', { error });
        });
      })
      .catch((error) => {
        logger.error('Error loading fingerprint for PartyKit logout', { error });
      });
  }
  
  // Set a logout flag FIRST to prevent accepting cached tokens from PartyKit
  // Use localStorage (not localforage) so it survives localforage.clear()
  const logoutTimestamp = Date.now();
  localStorage.setItem('userLoggedOut', String(logoutTimestamp));
  localStorage.removeItem('auth_token_backup'); // Clear token backup
  localStorage.removeItem('loginTime'); // Clear login time
  logger.info('Set logout flag to reject cached tokens', { logoutTimestamp });
  
  // Clear all local data completely (not preserving anything)
  store.set(configStore.set, 'config', () => defaultConfig);
  store.set(pageStore.clear);
  
  // Clear localforage completely
  await localforage.clear().catch((error) => {
    logger.error('Error clearing localforage', { error });
  });
  
  // Clear all IndexedDB databases
  try {
    const databases = await indexedDB.databases();
    const dbNames = databases.map(d => d.name);
    logger.info('Clearing IndexedDB databases', { databases: dbNames });
    
    const deletionPromises = databases
      .filter(db => db.name)
      .map(db => {
        return new Promise<void>((resolve) => {
          const request = indexedDB.deleteDatabase(db.name!);
          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
          request.onblocked = () => resolve();
        });
      });
    
    await Promise.all(deletionPromises);
    logger.info('All databases cleared');
  } catch (error) {
    logger.error('Error clearing IndexedDB', { error });
  }
  
  // Clear Auth0 session by opening logout URL
  try {
    const env = getEnvironment(window.location.hostname);
    const returnToUrl = encodeURIComponent(window.location.origin);
    const auth0LogoutUrl = `${AUTH0_DOMAINS[env]}/v2/logout?client_id=${AUTH0_CLIENT_IDS[env]}&returnTo=${returnToUrl}`;
    logger.info('Opening Auth0 logout', { auth0LogoutUrl, env });
    
    if (typeof Office !== 'undefined' && Office?.context?.ui?.openBrowserWindow) {
      // Use Office API to open browser window
      logger.info('Using Office.context.ui.openBrowserWindow for Auth0 logout');
      Office.context.ui.openBrowserWindow(auth0LogoutUrl);
      
      // Reload after a short delay to allow Auth0 logout to complete
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      // Fallback: try window.open, then redirect
      logger.info('Trying window.open for Auth0 logout');
      const popup = window.open(auth0LogoutUrl, '_blank');
      
      if (popup) {
        // Popup opened, reload after delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        // Popup blocked, redirect instead
        logger.info('Popup blocked, redirecting to Auth0 logout');
        window.location.replace(auth0LogoutUrl);
      }
    }
  } catch (error) {
    logger.error('Error during Auth0 logout', { error });
    // Fallback to reload if logout fails
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
}

function validate<T>(zodSchema: ZodSchema<T>, item: T) {
  if (isDev) {
    return item;
  } else {
    return zodSchema.parse(item);
  }
}

// Simple atom for pages as backup (jotai-minidb has reactivity issues)
const pagesBackupAtom = atom<TPage[]>([]);
pagesBackupAtom.debugLabel = 'pagesBackupAtom';

export function usePages() {
  // Read from both MiniDb and backup atom
  const miniDbPages = useAtomValue(pageStore.values);
  const backupPages = useAtomValue(pagesBackupAtom);
  const setBackupPages = useSetAtom(pagesBackupAtom);
  const setMany = useSetAtom(pageStore.setMany);
  
  // Use whichever has data (backup takes priority as it's more reliable)
  const currentPages = (backupPages.length > 0 ? backupPages : miniDbPages).sort(
    (a, b) => (a?.index ?? 0) - (b?.index ?? 0),
  );
  
  const setPagesFn = (pages: TPage[]) => {
    const valid_pages = validate(z.array(pageSchema), pages);
    const newPages: [string, TPage][] = valid_pages.map((page, i) => {
      return [page.id, { ...page, index: i }];
    });
    const pagesWithIndex = newPages.map(([, page]) => page);
    
    // Set to both MiniDb (for persistence) and backup atom (for reactivity)
    setMany(newPages);
    setBackupPages(pagesWithIndex);
  };
  return [currentPages, setPagesFn] as const;
}

export const selectedPageIdsAtom = atom((get) => {
  const config = get(configStore.item('config'));
  return config?.selectedPageIds ?? [];
});
selectedPageIdsAtom.debugLabel = 'selectedPageIdsAtom';

export function useSelectedPageIds() {
  const selectedPages = useAtomValue(selectedPageIdsAtom);
  const setPages = useSetAtom(configStore.set);
  const setPagesFn = (pages: TPage[]) => {
    const valid_pages = validate(z.array(pageSchema), pages);
    const pageIds = valid_pages.map((page) => page.id);
    logger.debug('setPagesFn', { pageIds });
    setPages('config', (prev) => {
      return {
        ...(prev ?? defaultConfig),
        selectedPageIds: pageIds,
      };
    });
  };
  return [selectedPages, setPagesFn] as const;
}

export function useSelectedPages() {
  const [pageIds, setPages] = useSelectedPageIds();
  const [pages] = usePages();
  const selectedPages = pages.filter((page) => pageIds.includes(page.id));
  return [selectedPages, setPages] as const;
}

const tokenAtom = atom((get) => {
  const config = get(configStore.item('config'));
  return config?.token;
});

// Token atom that checks both store AND localStorage backup
// This ensures token is available even before jotai-minidb hydrates
export const tokenAtomWithBackup = atom((get) => {
  // First try the store token
  const storeToken = get(configStore.item('config'))?.token;
  if (storeToken) {
    return storeToken;
  }
  
  // If no store token, check localStorage backup (but not if logged out)
  const userLoggedOut = localStorage.getItem('userLoggedOut');
  if (userLoggedOut) {
    return '';
  }
  
  const backup = localStorage.getItem('auth_token_backup');
  return backup || '';
});
tokenAtomWithBackup.debugLabel = 'tokenAtomWithBackup';

export function useToken() {
  const token = useAtomValue(tokenAtomWithBackup);
  const setToken = useSetAtom(configStore.set);
  
  const setTokenFn = (newToken: string) => {
    // Save to localStorage as backup
    localStorage.setItem('auth_token_backup', newToken);
    // Save to store (this will update tokenAtom and all dependent atoms)
    setToken('config', (prev) => {
      return {
        ...(prev ?? defaultConfig),
        token: newToken,
      };
    });
  };
  
  return [token, setTokenFn] as const;
}

export const decodedToken = atom((get) => {
  const token = get(tokenAtomWithBackup);
  if (!token) return undefined;
  try {
    const decoded = jwtDecode(token) as JWT;
    const checks = checkToken({ decoded });
    if (checks?.error) {
      logger.error('useDecodedToken error', { checks });
      return {
        error: checks.error,
      };
    }
    if (checks?.tokenMsToExpiry) {
      localforage.setItem('tokenExpMs', checks.tokenMsToExpiry);
      return {
        decoded,
        error: checks.error,
        rawToken: token,
        expiresInMs: checks.tokenMsToExpiry,
      };
    } else {
      logger.warn('token expired', { decoded, checks });
    }
  } catch (error) {
    logger.error('useDecodedToken error', { error });
    return undefined;
  }
});

export const user = atom((get) => {
  const decoded = get(decodedToken);
  return {
    id: decoded?.decoded?.sub,
    error: decoded?.error,
    expiresInMs: decoded?.expiresInMs,
    rawToken: decoded?.rawToken,
    email:
      decoded?.decoded?.['https://hasura.io/jwt/claims']['x-hasura-user-email'],
  };
});

export function useUser() {
  const userAtom = useAtomValue(user) ?? {
    expiresInMs: 0,
    id: '',
    rawToken: '',
    error: null,
    email: '',
  };
  return userAtom;
}

export function useDecodedToken() {
  return useAtomValue(decodedToken);
}

export const lastUpdatedAtom = atom((get) => {
  const config = get(configStore.item('config'));
  const lastUpdatedMs = config?.lastUpdated;
  if (!lastUpdatedMs) return undefined;
  return new Date(lastUpdatedMs);
});
lastUpdatedAtom.debugLabel = 'lastUpdatedAtom';

export function useLastUpdated() {
  const value = useAtomValue(lastUpdatedAtom);
  const setConfig = useSetAtom(configStore.set);
  const setLastUpdatedFn = (lastUpdated: number) => {
    setConfig('config', (prev) => {
      return {
        ...(prev ?? defaultConfig),
        lastUpdated,
      };
    });
  };
  return [value, setLastUpdatedFn] as const;
}

const hasuraName = atom((get) => {
  const decoded = get(decodedToken);
  if (!decoded) {
    logger.warn('No decoded token found');
    return undefined;
  }
  if (decoded.error) {
    logger.error('decoded token error', { error: decoded.error });
    return 'uat';
  }
  const org_id = decoded.decoded?.org_id;
  if (!org_id) {
    logger.error('No org_id found in decoded token');
    return undefined;
  }
  return getHasuraName(org_id);
});
hasuraName.debugLabel = 'hasuraNameAtom';

export function useHasuraName() {
  return useAtomValue(hasuraName);
}

export function useExcelSheets() {
  const sheets = useAtomValue(configStore.item('config'))?.openWorkSheets;
  const setSheets = useSetAtom(configStore.set);
  const setSheetsFn = (sheets: string[]) => {
    const newSheets = validate(z.array(z.string()), sheets);
    setSheets('config', (prev) => {
      if (!prev) {
        return defaultConfig;
      }
      return {
        ...prev,
        openWorkSheets: newSheets,
      };
    });
  };
  return [sheets ?? [], setSheetsFn] as const;
}

const flattenPackagesToProducts = (packages: TPackage[]) => {
  return packages
    .map((pack) => {
      const source = pack.source.name;
      const products = pack.products.map((prod) => {
        return {
          ...prod,
          source,
        };
      });
      return products;
    })
    .flat();
};

export const productsAtom = atom((get) => {
  const config = get(configStore.item('config'));
  const packages = config?.packages ?? [];
  const products = flattenPackagesToProducts(packages);
  return products;
});
productsAtom.debugLabel = 'productsAtom';

export function useProducts() {
  return useAtomValue(productsAtom);
}

export function useSetPackages() {
  const setPackages = useSetAtom(configStore.set);
  const setPackagesFn = (packages: TPackage[]) => {
    const valid_packages = validate(z.array(packageSchema), packages);
    setPackages('config', (prev) => {
      return {
        ...(prev ?? defaultConfig),
        packages: valid_packages,
      };
    });
  };
  return setPackagesFn;
}

export const readyAtom = atom(false);

export function useReady() {
  return useAtomValue(readyAtom);
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <DevTools isInitialOpen={false} />
      {children}
    </>
  );
}
